/*
 * File: FindStory.styles.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 22, 2025 at 12:00 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import styled from 'styled-components';

// Constants
import { AnimationPanelWidth, xLargeBreakpoint } from '../../../styles/constants';


/**
 * Constants
 */

const ANIMATION_DURATION = '1s';


/**
 * Styles
 */

export const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	padding: 20px 0px 0px;
`;

export const ContainerPanel = styled.div`
	height: 100%;
	flex-grow: 1;
	position: relative;

	& > div {
		align-items: center;
		justify-content: center;
		height: 100%;
	}
`;

export const AnimationPanel = styled.div`
	width: ${AnimationPanelWidth}px;
	height: 100%;
	justify-content: center;
	flex-shrink: 0;
	display: none;
	border-radius: 20px;
	overflow: hidden;
	position: relative;

	@media only screen and (min-width: ${xLargeBreakpoint}em) {
		display: flex;
	}
`;

export const FormContainer = styled.div`
	width: 100%;
	max-width: 700px;
	padding: 50px 0px 50px;
`;

export const ContentContainer = styled.div`
	p {
		margin: 10px 0px 0px;
	}
	br {
		margin: 10px;
	}
	a,
	a:link,
	a:visited {
		text-decoration: underline;
	}
	p:not(:first-child) {
		margin: 10px 0px 0px;
	}
`;

export const InputContainer = styled.div`
	margin: 30px 0px 40px;

	input[type=text] {
		height: 55px;
		max-width: 500px;
	}
`;

export const SkyContainer = styled.div`
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;

	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
	&.step {
		background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(253, 240, 212, 1) 40%, rgba(255, 253, 253, 1) 55%, rgba(134, 156, 217, 1) 100%);
	}
`;

export const Sky = styled.div`
	background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(255, 253, 253, 1) 50%, rgba(134, 156, 217, 1) 100%);
	opacity: 1;
`;

export const SunContainer = styled.div`
	width: 120px;
	position: absolute;
	z-index: 1;
	transition: all ${ANIMATION_DURATION} ease-in-out;

	&.step {
		height: 120px;
		bottom: 560px;
	}
`;

export const SunInnerContainer = styled.div`
	width: 120px;
	height: 120px;
	border-radius: 50%;
	transition: all ${ANIMATION_DURATION} ease-in-out;
	position: relative;

	&.step {
		background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	}
	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
`;

export const Sun = styled.div`
	background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	opacity: 1;
	border-radius: 50%;
	box-shadow: 0px 0px 107px 57px rgba(255, 255, 255, 1);
	box-shadow:
        0 0 60px 30px rgba(255, 249, 237, 0.7),
        0 0 100px 60px rgba(255, 249, 237, 0.7),
        0 0 140px 90px rgba(255, 249, 237, 0.7);
`;

export const SunReflectionContainer = styled.div`
	width: 120px;
	height: 30px;
	position: absolute;
	z-index: 2;
	overflow: hidden;
	transform: scaleY(-1);
	transition: all ${ANIMATION_DURATION} ease-in-out;

	&.step {
		bottom: -180px;
	}
`;

export const SunReflectionInnerContainer = styled.div`
	width: 120px;
	height: 120px;
	border-radius: 50%;
	opacity: 0.4;
	mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px), linear-gradient(transparent 50%, white 90%);
	transition: all ${ANIMATION_DURATION} ease-in-out;
	position: relative;
	overflow: hidden;

	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
	&.step {
		mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px);
		opacity: 0;
	}
`;

export const OceanContainer = styled.div`
	height: 220px;
	width: 100%;
	position: absolute;
	bottom: 0px;
	left: 0px;
	z-index: 1;

	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
	&.step {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(255, 234, 219, 1) 120%);
	}
`;

export const Ocean = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(222, 236, 255, 1) 120%);
	opacity: 1;
`;

export const ActionContainer = styled.div`
	margin: 30px 0px 0px;
	display: flex;
	gap: 10px;
`;

export const LegalContainer = styled.div`
	margin: 30px 0px 0px;

	p {
		margin: 10px 0px 0px;
	}
	br {
		margin: 10px;
	}
`;
